import { supabase, run } from './db';
import { apagarArquivos } from './comprovantes';

/**
 * Recebimentos: o que de fato entrou, parcela por parcela.
 *
 * DOIS VALORES, E É A DECISÃO CENTRAL DESTE ARQUIVO.
 *
 *   `valor`         — quanto o CLIENTE pagou. É isto que abate a dívida dele.
 *   `valor_liquido` — quanto entrou na CONTA, depois de taxa e antecipação.
 *
 * Uma parcela de R$ 1.000 antecipada rende R$ 940. Se a quitação olhasse o
 * líquido, ela nunca fecharia e sobraria uma inadimplência de R$ 60 que o
 * cliente não deve — por isso antecipar MARCA COMO PAGO. A diferença é
 * despesa financeira do laboratório, e fica visível como tal.
 *
 * `paga_em` na parcela não é mais clicado: virou consequência, escrita por
 * gatilho quando a soma dos recebimentos alcança o valor devido. Todos os
 * lugares que já liam `paga_em` continuam funcionando, e passam a estar certos
 * — parcela recebida pela metade deixou de contar como paga.
 *
 * Nada aqui é enfileirável. Dinheiro que entrou é fato do escritório, com
 * sinal; e uma baixa enfileirada que sobe depois de outra pessoa ter lançado a
 * mesma faria o cliente pagar duas vezes no relatório.
 */

const num = (v) => Number(v) || 0;

/**
 * Centavos inteiros. TODA comparação de dinheiro aqui passa por isto.
 *
 * O Postgres entrega `numeric` como string e o JS a transforma em float. Uma
 * parcela de R$ 1.000,10 recebida em 333,37 + 333,37 + 333,36 soma
 * 1000.0999999999999 — e `recebido >= valor` dava FALSO. A tela dizia
 * "parcial" enquanto o gatilho do banco, que soma em `numeric`, já tinha
 * carimbado a quitação. Tela e banco discordando sobre a mesma parcela.
 *
 * O mesmo resíduo aparecia como saldo de 1.1368683772161603e-13, que ia
 * escrito assim no campo "valor recebido" do formulário, e fazia o atalho de
 * quitar o serviço tentar inserir um recebimento de zero — recusado pelo
 * banco, com erro cru, numa parcela que já estava paga.
 */
const cents = (v) => Math.round(num(v) * 100);

/** Soma de dinheiro, em reais, sem resíduo de float. */
const somaEmReais = (valores = []) =>
  valores.reduce((s, v) => s + cents(v), 0) / 100;

export const listarRecebimentos = (installmentIds = []) =>
  installmentIds.length === 0 ? Promise.resolve([])
    : run(supabase
        .from('recebimentos')
        .select('*')
        .in('installment_id', installmentIds)
        .order('recebido_em', { ascending: false }));

export const lancarRecebimento = (row) =>
  run(supabase.from('recebimentos').insert([row]).select().single());

/**
 * Apaga o lançamento — e o comprovante junto.
 *
 * Não há cascata do banco para o Storage. Sem esta busca antes do delete, o
 * arquivo ficaria no bucket sem nenhuma linha que o mencione: invisível na
 * tela, presente na conta, e sem como saber de quem era.
 */
export const apagarRecebimento = async (id) => {
  const alvo = await run(supabase
    .from('recebimentos').select('comprovante_path').eq('id', id).maybeSingle());
  await run(supabase.from('recebimentos').delete().eq('id', id));
  await apagarArquivos([alvo?.comprovante_path]);
};

/** Parcelas vivas de um serviço: faturas de serviço, não canceladas. */
export const parcelasDoServico = (serviceId) =>
  run(supabase
    .from('invoice_installments')
    .select('id, valor, invoice_id, invoices!inner(service_id, tipo, cancelada_em)')
    .eq('invoices.service_id', serviceId)
    .eq('invoices.tipo', 'servico')
    .is('invoices.cancelada_em', null));

/**
 * Quita o serviço inteiro de uma vez.
 *
 * ATALHO, NÃO SEGUNDA VERDADE. Marcar um `services.paid` à mão criaria um
 * campo que diz "pago" enquanto o saldo do cliente diz que falta receber — e
 * aí não há como saber qual dos dois está certo.
 *
 * Em vez disso, o check LANÇA: cria um recebimento do que falta em cada
 * parcela aberta. A quitação continua vindo da soma, o saldo continua batendo,
 * e quem quiser o detalhe (meio, conta, antecipação) abre a parcela e edita.
 *
 * É o caminho para o pagamento que aconteceu fora do sistema — dinheiro,
 * permuta, acerto de boca — onde abrir quatro lançamentos é atrito sem ganho.
 */
export const quitarServico = async (laboratoryId, serviceId, opcoes = {}) => {
  const parcelas = await parcelasDoServico(serviceId) || [];
  if (parcelas.length === 0) {
    throw new Error('Este serviço ainda não tem fatura emitida.');
  }

  const recebidos = await listarRecebimentos(parcelas.map(p => p.id));
  const porParcela = {};
  (recebidos || []).forEach(r => {
    porParcela[r.installment_id] = (porParcela[r.installment_id] || 0) + num(r.valor);
  });

  const novos = parcelas
    .map(p => ({ parcela: p, falta: num(p.valor) - (porParcela[p.id] || 0) }))
    .filter(x => x.falta > 0)
    .map(x => ({
      laboratory_id:  laboratoryId,
      installment_id: x.parcela.id,
      recebido_em:    opcoes.recebido_em || new Date().toISOString().slice(0, 10),
      valor:          Math.round(x.falta * 100) / 100,
      meio:           opcoes.meio || null,
      conta_id:       opcoes.conta_id || null,
      observacao:     opcoes.observacao || null,
    }));

  if (novos.length === 0) return { lancados: 0 };
  await run(supabase.from('recebimentos').insert(novos));
  return { lancados: novos.length };
};

/**
 * Desfaz a quitação: apaga os recebimentos das parcelas do serviço.
 *
 * Apaga TODOS, e não só os que o atalho criou — não há como distinguir, e
 * fingir que há seria pior. Por isso a tela confirma dizendo quantos somem.
 */
export const desfazerQuitacaoDoServico = async (serviceId) => {
  const parcelas = await parcelasDoServico(serviceId) || [];
  if (parcelas.length === 0) return { apagados: 0 };
  const ids = parcelas.map(p => p.id);
  const recebidos = await listarRecebimentos(ids);
  if ((recebidos || []).length === 0) return { apagados: 0 };
  await run(supabase.from('recebimentos').delete().in('installment_id', ids));
  // Mesma razão do `apagarRecebimento`: o Storage não sabe que a linha sumiu.
  await apagarArquivos((recebidos || []).map(r => r.comprovante_path));
  return { apagados: recebidos.length };
};

// ── Contas onde o dinheiro entra ──────────────────────────────────────────

export const listarContas = (laboratoryId) =>
  run(supabase
    .from('contas_bancarias')
    .select('*')
    .eq('laboratory_id', laboratoryId)
    .order('ativa', { ascending: false })
    .order('apelido'));

export const criarConta = (row) =>
  run(supabase.from('contas_bancarias').insert([row]).select().single());

export const alternarConta = (id, ativa) =>
  run(supabase.from('contas_bancarias').update({ ativa }).eq('id', id));

/**
 * Exclui a conta.
 *
 * A FK de `recebimentos` é RESTRICT: conta com movimento recusa a exclusão, e
 * é o certo — apagar levaria junto a resposta de "para onde foi esse
 * dinheiro". Para conta que deixou de existir, desativar.
 */
export const apagarConta = (id) =>
  run(supabase.from('contas_bancarias').delete().eq('id', id));

// ── Saldo ─────────────────────────────────────────────────────────────────

/**
 * Parcelas que ainda devem dinheiro, com saldo e vencimento.
 *
 * Sem `cached` como o resto do financeiro: saldo apresentado velho leva a
 * cobrar quem já pagou.
 */
export const listarParcelasEmAberto = (laboratoryId) =>
  run(supabase
    .from('vw_parcelas_em_aberto')
    .select('*')
    .eq('laboratory_id', laboratoryId)
    .order('vencimento'));

export const listarSaldoPorCliente = (laboratoryId) =>
  run(supabase
    .from('vw_saldo_por_cliente')
    .select('*')
    .eq('laboratory_id', laboratoryId)
    .order('saldo', { ascending: false }));

/**
 * Cada recebimento, com a data e se a fatura dele continua viva.
 *
 * Fatura cancelada fica de fora, como na `vw_saldo_por_cliente`: senão o
 * total por mês e o "Recebido" da mesma tela discordariam.
 */
export const listarRecebidoNoTempo = (laboratoryId) =>
  run(supabase
    .from('recebimentos')
    .select('recebido_em, valor, invoice_installments!inner(invoices!inner(cancelada_em))')
    .eq('laboratory_id', laboratoryId));

// ── Regras puras, testáveis ───────────────────────────────────────────────

/**
 * Quanto falta receber de uma parcela.
 *
 * Sobre o `valor` dos recebimentos, nunca sobre o líquido: o cliente não deve
 * a taxa de antecipação.
 */
export const saldoDaParcela = (parcela, recebimentos = []) =>
  Math.max(0, (cents(parcela?.valor) - recebimentos.reduce((s, r) => s + cents(r.valor), 0)) / 100);

/**
 * Como a parcela está: aberta, parcial ou quitada.
 *
 * "parcial" existe porque antes ela não existia — recebimento pela metade era
 * indistinguível de nenhum recebimento, e o Financeiro mostrava as duas como
 * "a receber" pelo valor cheio.
 */
export const situacaoDaParcela = (parcela, recebimentos = []) => {
  const recebido = recebimentos.reduce((s, r) => s + cents(r.valor), 0);
  if (recebido <= 0) return 'aberta';
  return recebido >= cents(parcela?.valor) ? 'quitada' : 'parcial';
};

/**
 * O que o laboratório perdeu para taxas e antecipações.
 *
 * Só conta o que foi informado: `valor_liquido` nulo significa "entrou tudo",
 * não "entrou zero".
 */
export const custoFinanceiro = (recebimentos = []) =>
  recebimentos.reduce((s, r) => {
    const liquido = r.valor_liquido === null || r.valor_liquido === undefined
      ? cents(r.valor) : cents(r.valor_liquido);
    return s + (cents(r.valor) - liquido);
  }, 0) / 100;

/** Os números do painel de saldo. */
export const resumoDoSaldo = (linhas = []) => ({
  clientes:  linhas.filter(l => num(l.saldo) > 0).length,
  faturado:  somaEmReais(linhas.map(l => l.faturado)),
  recebido:  somaEmReais(linhas.map(l => l.recebido)),
  saldo:     somaEmReais(linhas.map(l => l.saldo)),
  vencido:   somaEmReais(linhas.map(l => l.vencido)),
  // As duas metades do faturado, porque o Dashboard mostra SÓ a de serviço e
  // os dois números apareciam na mesma sessão sem nada explicando a diferença.
  faturadoServico: somaEmReais(linhas.map(l => l.faturado_servico)),
  faturadoEntrada: somaEmReais(linhas.map(l => l.faturado_entrada)),
});

/**
 * QUANDO o dinheiro entra, em quatro baldes.
 *
 * O painel tinha quatro números sem dimensão de tempo: "a receber R$ 37.575"
 * não distingue o que vence depois de amanhã do que vence em novembro, e é
 * essa diferença que decide o que se faz hoje.
 *
 * Os baldes não se sobrepõem e somam o total, para o número grande poder ser
 * conferido de cabeça na tela.
 *
 * `vencido` conta por PARCELA. O painel somava o saldo da fatura inteira
 * quando a primeira parcela vencia — numa 4x com a primeira atrasada, as
 * outras três entravam como vencidas.
 */
export const agendaDeRecebimento = (parcelas = [], hoje = new Date()) => {
  const h = hoje.toISOString().slice(0, 10);
  const limite = (dias) => {
    const d = new Date(`${h}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + dias);
    return d.toISOString().slice(0, 10);
  };
  const em7 = limite(7);
  const em30 = limite(30);

  const baldes = { vencido: 0, ate7: 0, ate30: 0, depois: 0, semData: 0 };

  (parcelas || []).forEach((p) => {
    const saldo = cents(p?.saldo);
    if (saldo <= 0) return;
    const venc = p?.vencimento ? String(p.vencimento).slice(0, 10) : null;

    // Sem vencimento é um balde próprio, e não "depois": a fatura sem data
    // nunca vence, então ela some de toda cobrança em vez de aparecer no fim
    // da fila. Separada, ela vira a pergunta que é.
    if (!venc) baldes.semData += saldo;
    else if (venc < h) baldes.vencido += saldo;
    else if (venc <= em7) baldes.ate7 += saldo;
    else if (venc <= em30) baldes.ate30 += saldo;
    else baldes.depois += saldo;
  });

  const emReais = Object.fromEntries(
    Object.entries(baldes).map(([k, v]) => [k, v / 100]));

  return {
    ...emReais,
    total: Object.values(baldes).reduce((s, v) => s + v, 0) / 100,
  };
};

/**
 * O que entrou em cada mês, do mais recente para o mais antigo.
 *
 * Pela data do RECEBIMENTO, não da fatura nem do vencimento: a pergunta é
 * quando o dinheiro chegou. E pelo `valor`, que é o que o cliente pagou, para
 * fechar com o "Recebido" da `vw_saldo_por_cliente`; o líquido é outra conta.
 *
 * O mês vem da string da data, sem passar por `Date`: um `date` do Postgres
 * lido como meia-noite UTC cai no dia anterior no Brasil, e o dia 1º ia parar
 * no mês de antes.
 */
export const recebidoPorMes = (recebimentos = []) => {
  const porMes = {};
  recebimentos.forEach(r => {
    if (r?.invoice_installments?.invoices?.cancelada_em) return;
    const mes = String(r?.recebido_em || '').slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(mes)) return;
    porMes[mes] = (porMes[mes] || 0) + cents(r.valor);
  });
  return Object.keys(porMes).sort().reverse()
    .map(mes => ({ mes, valor: porMes[mes] / 100 }));
};
